import { SFX_MANIFEST, type SfxId } from '../data/audio-manifest';
import type { AudioBufferLike, AudioEngine, BufferSourceLike } from './engine';
import { renderSfx } from './synth';

/** One sound that played, for the test hooks (issue #76). */
export interface SfxLogEntry {
  id: SfxId;
  bus: 'sfx';
  /** Gain of the bus at that moment: 0 when the effects volume is 0. */
  effectiveGain: number;
  tick: number;
}

export interface SfxPlayer {
  play(id: SfxId, tick?: number): void;
  readonly log: readonly SfxLogEntry[];
  /** Forgets the log: a new level run starts a new one, so two runs with the same seed log the same. */
  clearLog(): void;
}

/** At most this many copies of the same sound at once; the next one is dropped (many bills in one tick). */
export const MAX_POLYPHONY = 4;
const LOG_LIMIT = 200;

/**
 * Plays the effects on the `sfx` bus of the audio engine. The samples are rendered once per effect and
 * kept as buffers; each play creates a one-shot source. Without audio every call is a silent no-op.
 */
export function createSfxPlayer(engine: Pick<AudioEngine, 'state' | 'context' | 'buses' | 'sfxGain'>): SfxPlayer {
  const buffers = new Map<SfxId, AudioBufferLike>();
  const endTimes = new Map<SfxId, number[]>();
  const log: SfxLogEntry[] = [];

  const bufferFor = (id: SfxId): AudioBufferLike | null => {
    const cached = buffers.get(id);
    if (cached) return cached;
    const context = engine.context;
    const entry = SFX_MANIFEST.find((candidate) => candidate.id === id);
    if (!context || !entry) return null;
    const samples = renderSfx(entry.params, context.sampleRate);
    const buffer = context.createBuffer(1, samples.length, context.sampleRate);
    buffer.getChannelData(0).set(samples);
    buffers.set(id, buffer);
    return buffer;
  };

  return {
    log,
    clearLog() {
      log.length = 0;
    },
    play(id, tick = 0) {
      const context = engine.context;
      const buses = engine.buses;
      if (engine.state === 'unavailable' || !context || !buses) return;
      try {
        const buffer = bufferFor(id);
        if (!buffer) return;
        const now = context.currentTime;
        const playing = (endTimes.get(id) ?? []).filter((end) => end > now);
        if (playing.length >= MAX_POLYPHONY) return;
        const source: BufferSourceLike = context.createBufferSource();
        source.buffer = buffer;
        source.connect(buses.sfx);
        source.start();
        endTimes.set(id, [...playing, now + buffer.duration]);
        log.push({ id, bus: 'sfx', effectiveGain: engine.sfxGain, tick });
        if (log.length > LOG_LIMIT) log.shift();
      } catch {
        // Audio must never break the game.
      }
    },
  };
}
